import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { ago, dateTime, humanise, money } from '../../lib/format.js'
import { Badge, Drawer, DrawerSection, Empty, KeyValue, Notice, RowsSkeleton, Switch } from '../../components/ui.js'
import { ConfirmDialog, toast } from '../../components/feedback.js'
import { MailIcon, WhatsAppIcon } from '../../components/icons.js'
import { MESSAGE_TONE, newIdempotencyKey, type Channel, type CheckoutDetail } from './types.js'

/**
 * One abandoned checkout, in full: who, what was in the cart, whether they can
 * be contacted and why not, everything sent to them and what happened to it,
 * and the orders that followed. Contact details arrive masked for a view-only
 * role — the server decides, the drawer only renders.
 */
export function CustomerDrawer({ id, onClose }: { id: string | null; onClose: () => void }): ReactNode {
  const queryClient = useQueryClient()
  const [confirm, setConfirm] = useState<Channel | null>(null)

  const detail = useQuery({
    queryKey: ['abandoned-detail', id],
    queryFn: () => api<CheckoutDetail>(`/v1/abandoned/${id}`),
    enabled: id !== null,
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['abandoned-detail', id] })
    void queryClient.invalidateQueries({ queryKey: ['abandoned'] })
  }

  const remind = useMutation({
    mutationFn: (channel: Channel) =>
      api<{ status: string; charged: string }>(`/v1/abandoned/${id}/remind`, { method: 'POST', body: { channel, idempotencyKey: newIdempotencyKey('manual') } }),
    onSuccess: (result, channel) => {
      setConfirm(null)
      const label = channel === 'whatsapp' ? 'WhatsApp' : 'Email'
      // "Sent" only when the provider accepted it; a deferred send says so.
      if (result.status === 'sent') toast('ok', `${label} sent`, Number(result.charged) > 0 ? `${money(result.charged)} charged to your wallet.` : undefined)
      else toast('info', `${label} queued`, 'Another message to this shopper is in progress; this one follows shortly.')
      refresh()
    },
    onError: (error) => {
      setConfirm(null)
      toast('error', 'Reminder not sent', (error as Error).message)
    },
  })

  const suppress = useMutation({
    mutationFn: (input: { channel: Channel; suppressed: boolean }) => api(`/v1/abandoned/${id}/suppress`, { method: 'POST', body: input }),
    onSuccess: (_result, input) => {
      toast('ok', input.suppressed ? 'Marked do-not-contact' : 'Contact allowed again')
      refresh()
    },
    onError: (error) => toast('error', 'Could not update', (error as Error).message),
  })

  const data = detail.data
  const canWrite = data?.access.canWrite ?? false

  return (
    <Drawer
      open={id !== null}
      onClose={onClose}
      wide
      title={data ? data.checkout.customerName ?? 'Guest shopper' : 'Checkout'}
      subtitle={data ? `Checkout #${data.checkout.id} · ${data.checkout.store ?? ''}` : undefined}
      footer={
        data && canWrite && !data.checkout.recovered ? (
          <>
            <button type="button" className="btn" disabled={!data.eligibility.whatsapp.eligible || remind.isPending} title={data.eligibility.whatsapp.reason ?? 'Send the WhatsApp reminder now'} onClick={() => setConfirm('whatsapp')}>
              <WhatsAppIcon size={15} /> Send WhatsApp
            </button>
            <button type="button" className="btn" disabled={!data.eligibility.email.eligible || remind.isPending} title={data.eligibility.email.reason ?? 'Send the email reminder now'} onClick={() => setConfirm('email')}>
              <MailIcon size={15} /> Send email
            </button>
          </>
        ) : undefined
      }
    >
      {detail.isPending && <RowsSkeleton rows={8} />}
      {detail.error && (
        <Empty error title="Could not load this checkout">
          {(detail.error as Error).message}
        </Empty>
      )}

      {data && (
        <>
          {data.access.masked && <Notice kind="info">Contact details are hidden for view-only access.</Notice>}

          <DrawerSection title="Customer">
            <KeyValue
              rows={[
                { label: 'Name', value: data.checkout.customerName ?? 'Guest shopper' },
                { label: 'Phone', value: <>{data.checkout.phone ?? '—'} {data.checkout.verified && <Badge tone="ok">Verified</Badge>}</> },
                { label: 'Email', value: data.checkout.email ?? '—' },
                { label: 'Customer', value: data.checkout.returning ? 'Returning' : 'New' },
                { label: 'Marketing consent', value: data.checkout.marketingConsent === null ? 'Not asked' : data.checkout.marketingConsent ? 'Yes' : 'Refused' },
              ]}
            />
          </DrawerSection>

          <DrawerSection title={`Cart (${data.cart.length})`}>
            <ul className="cart-list">
              {data.cart.map((item, index) => (
                <li key={index}>
                  {item.image ? <img src={item.image} alt="" width={36} height={36} loading="lazy" /> : <span className="cart-list__ph" aria-hidden />}
                  <span className="list__text">
                    <strong>{item.title}</strong>
                    <small>
                      {item.variant ? `${item.variant} · ` : ''}Qty {item.quantity}
                    </small>
                  </span>
                  {item.price && <span className="num">{money(item.price)}</span>}
                </li>
              ))}
            </ul>
            <KeyValue
              rows={[
                ...(data.totals.subtotal ? [{ label: 'Subtotal', value: money(data.totals.subtotal) }] : []),
                ...(data.totals.discount && Number(data.totals.discount) > 0 ? [{ label: 'Discount', value: `−${money(data.totals.discount)}` }] : []),
                ...(data.totals.shipping ? [{ label: 'Shipping', value: money(data.totals.shipping) }] : []),
                { label: 'Cart value', value: money(data.totals.total), kind: 'total' as const },
              ]}
            />
          </DrawerSection>

          <DrawerSection title="Status">
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <Badge tone={data.checkout.recovered ? 'ok' : 'warn'}>{data.checkout.recovered ? 'Recovered' : 'Not recovered'}</Badge>
              <Badge tone="info">Stopped at {data.checkout.stage}</Badge>
              {data.checkout.paymentStatus !== 'none' && <Badge tone={data.checkout.paymentStatus === 'failed' ? 'bad' : 'warn'}>Payment {data.checkout.paymentStatus}</Badge>}
              {data.checkout.clicks > 0 && <Badge tone="ok">Link opened {data.checkout.clicks}×</Badge>}
            </div>
            <p className="muted small">
              Abandoned {ago(data.checkout.at)} · started {dateTime(data.checkout.createdAt)}
            </p>
          </DrawerSection>

          <DrawerSection title="Contact eligibility">
            {(['whatsapp', 'email'] as const).map((channel) => (
              <div key={channel} className="elig-row">
                <span className="elig-row__label">
                  {channel === 'whatsapp' ? <WhatsAppIcon size={15} /> : <MailIcon size={15} />}
                  {channel === 'whatsapp' ? 'WhatsApp' : 'Email'}
                </span>
                <Badge tone={data.eligibility[channel].eligible ? 'ok' : 'warn'}>{data.eligibility[channel].eligible ? 'Can be contacted' : data.eligibility[channel].reason}</Badge>
                {canWrite && (
                  <span className="elig-row__toggle">
                    <small>Do not contact</small>
                    <Switch label={`Do not contact on ${channel}`} checked={data.suppressed[channel]} disabled={suppress.isPending} onChange={(value) => suppress.mutate({ channel, suppressed: value })} />
                  </span>
                )}
              </div>
            ))}
          </DrawerSection>

          <DrawerSection title={`Messages (${data.messages.length})`}>
            {data.messages.length === 0 ? (
              <p className="muted small">No recovery message has been sent for this checkout.</p>
            ) : (
              <ul className="msg-list">
                {data.messages.map((message) => (
                  <li key={message.id}>
                    <span className="msg-list__icon">{message.channel === 'whatsapp' ? <WhatsAppIcon size={14} /> : <MailIcon size={14} />}</span>
                    <span className="list__text">
                      <strong>
                        {message.source}
                        {message.template ? ` · ${message.template}` : ''}
                      </strong>
                      <small>
                        {message.sentAt ? `Sent ${dateTime(message.sentAt)}` : message.failedAt ? `Failed ${dateTime(message.failedAt)}` : humanise(message.status)}
                        {message.deliveredAt ? ' · delivered' : ''}
                        {message.readAt ? ' · read' : ''}
                        {message.clickedAt ? ` · clicked${message.clicks > 1 ? ` ${message.clicks}×` : ''}` : ''}
                        {Number(message.cost) > 0 ? ` · ${money(message.cost)}` : ''}
                      </small>
                      {(message.error ?? message.skipReason) && <small className="text-bad">{message.error ?? humanise(message.skipReason)}</small>}
                    </span>
                    <Badge tone={MESSAGE_TONE[message.status]}>{humanise(message.status)}</Badge>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted small">Delivered and read come only from WhatsApp’s own receipts. Email shows “sent” once the mail server accepts it.</p>
          </DrawerSection>

          <DrawerSection title="Timeline">
            <ol className="activity">
              {data.timeline.map((entry, index) => (
                <li key={index} className={entry.tone ? `activity__item activity__item--${entry.tone}` : 'activity__item'}>
                  <span className="activity__dot" aria-hidden />
                  <span className="list__text">
                    <strong>{entry.label}</strong>
                    <small>
                      {dateTime(entry.at)}
                      {entry.detail ? ` · ${entry.detail}` : ''}
                    </small>
                  </span>
                </li>
              ))}
            </ol>
          </DrawerSection>

          <DrawerSection title="Related orders">
            {data.orders.length === 0 ? (
              <p className="muted small">No orders from this checkout or shopper yet.</p>
            ) : (
              <ul className="msg-list">
                {data.orders.map((order) => (
                  <li key={order.id}>
                    <span className="list__text">
                      <strong>{order.orderNumber}</strong>
                      <small>
                        {dateTime(order.createdAt)} · {order.sameCheckout ? 'from this checkout' : 'another checkout'}
                      </small>
                    </span>
                    <span className="num">{money(order.total)}</span>
                    <Badge tone={order.paymentStatus}>{humanise(order.paymentStatus)}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </DrawerSection>
        </>
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={confirm === 'whatsapp' ? 'Send a WhatsApp reminder?' : 'Send an email reminder?'}
        confirmLabel="Send now"
        pending={remind.isPending}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && remind.mutate(confirm)}
        message={
          confirm === 'whatsapp'
            ? 'Sends your abandoned-checkout WhatsApp template to this shopper now. It is charged the per-message price from your wallet if WhatsApp accepts it.'
            : 'Sends the reminder email to this shopper now. Email is free.'
        }
      />
    </Drawer>
  )
}
